import React from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalFooter from './components/GlobalFooter';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Terms and <span className="text-green-600">Conditions</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Please read these terms and conditions carefully before using Food Connect services.
          </p>
          <p className="text-lg text-gray-500 mt-4">
            Last updated: October 2025
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none space-y-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-green-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">1</span>
                Food Connect Service Agreement
              </h2>
              <p className="text-gray-700 leading-relaxed">
                By registering with Food Connect, you agree to use our platform responsibly to connect surplus food 
                with those in need. Our service facilitates food rescue operations across India and Singapore, 
                helping reduce food waste while supporting communities.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-blue-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">2</span>
                User Responsibilities
              </h2>
              <div className="space-y-4 text-gray-700">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">🍽️ Restaurants</h4>
                  <p>Ensure all surplus food is safe, properly stored, and accurately described. Confirm food safety standards are met before listing items on the platform.</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">🤝 NGOs</h4>
                  <p>Accept food donations responsibly and distribute them safely to intended beneficiaries. Ensure proper handling and storage of received food items.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">🚚 Transporters</h4>
                  <p>Handle food items with care, maintain hygiene standards, and complete deliveries promptly. Follow all food safety protocols during transportation.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-orange-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">3</span>
                Food Safety and Quality
              </h2>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  All users must comply with local food safety regulations. Food donors must confirm safety standards, 
                  and recipients must inspect food upon delivery. Food Connect facilitates connections but users are 
                  responsible for food quality and safety.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  From the moment the food is accepted, whether by an NGO or a volunteer, full responsibility for its safe and timely handling and delivery rests with that party until it is transferred to the next designated partner.
                </p>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800 font-medium">⚠️ Important Safety Notice</p>
                  <p className="text-yellow-700 text-sm mt-1">
                    Always verify food quality, check expiration dates, and follow local health guidelines when handling food donations.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-red-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">4</span>
                Platform Usage Guidelines
              </h2>
              <div className="space-y-3 text-gray-700">
                <p>Users agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate and up-to-date information</li>
                  <li>Use the platform only for legitimate food rescue purposes</li>
                  <li>Respect other users and maintain professional communication</li>
                  <li>Report any issues or concerns promptly to Food Connect support</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-gray-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">5</span>
                Liability and Disclaimers
              </h2>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Food Connect serves as a platform to facilitate food rescue connections. Users participate at their own risk and 
                  responsibility. We are not liable for food quality, safety issues, delivery problems, or any damages arising from platform usage.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-600 text-sm">
                    <strong>Limitation of Liability:</strong> Food Connect's liability is limited to the maximum extent permitted by law. 
                    Users acknowledge that food donation and rescue activities involve inherent risks.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-indigo-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">6</span>
                Account Management
              </h2>
              <div className="space-y-3 text-gray-700">
                <p>Account holders are responsible for:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Maintaining the confidentiality of login credentials</li>
                  <li>All activities that occur under their account</li>
                  <li>Promptly notifying us of any unauthorized account access</li>
                  <li>Keeping profile information current and accurate</li>
                </ul>
                <p className="text-sm text-gray-600 mt-4">
                  Food Connect reserves the right to suspend or terminate accounts that violate these terms or engage in inappropriate behavior.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-teal-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">7</span>
                Changes to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Food Connect reserves the right to modify these terms at any time. Users will be notified of significant changes 
                via email or platform notifications. Continued use of the service after changes constitutes acceptance of the new terms.
              </p>
            </div>
            
            <div className="bg-green-600 text-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span className="text-3xl mr-3">📞</span>
                Contact Information
              </h2>
              <p className="mb-4">
                If you have questions about these Terms and Conditions, please contact us:
              </p>
              <div className="space-y-2">
                <p><strong>Email:</strong> legal@foodconnect.asia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
};

export default TermsAndConditions;